// Tool Wear Detection — visual wear-land VB measurement, life-used tracking,
// remaining-life prediction, replace-recommendation traffic light.
import React, { useEffect, useState } from 'react';
import {
  Box, Card, Typography, Button, TextField, MenuItem, Stack, Chip, LinearProgress,
} from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { customerbasedshift, shiftadd } from '../../Services/app/masterservice';
import { cleanCustomerId } from '../../Services/app/operatorservice';

const STORAGE_KEY = 'tool_wear_log';
const MACHINES = ['ATECH-V01','ATECH-V02','ATECH-V03','ATECH-V05','ATECH-V06','ATECH-V07','ATECH-V08','ATECH-T01','ATECH-T02','ATECH-T04'];
const TOOL_TYPES = ['CNMG 120408 (Turning insert)', 'VBMT 160404 (Turning insert)', 'APMT 1604 (Milling insert)', 'End Mill ø10', 'End Mill ø16', 'Drill HSS ø8.5', 'Reamer ø10 H7'];

const ToolWearScreen = () => {
  const cid = cleanCustomerId(localStorage.getItem('CustomerID'));
  const [history, setHistory] = useState([]);

  const [machine, setMachine] = useState('ATECH-V07');
  const [toolNo, setToolNo] = useState('T0808');
  const [toolType, setToolType] = useState(TOOL_TYPES[0]);
  const [vbMax, setVbMax] = useState(0.30); // Replace at VB = 0.30 mm (typical)
  const [vbWarn, setVbWarn] = useState(0.20);
  const [vbMeasured, setVbMeasured] = useState(0.18);
  const [partsCut, setPartsCut] = useState(180);
  const [partsLimit, setPartsLimit] = useState(250);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    (async () => {
      try { const r = await customerbasedshift(cid, STORAGE_KEY); setHistory(Array.isArray(r?.[0]?.value) ? r[0].value : []); } catch { setHistory([]); }
    })();
  }, [cid]);

  const vbPct = Math.min(100, (vbMeasured / vbMax) * 100);
  const lifePct = Math.min(100, (partsCut / partsLimit) * 100);
  // Predict remaining: assume linear wear rate
  const remainingParts = vbMeasured > 0 ? Math.max(0, Math.round(((vbMax - vbMeasured) / vbMeasured) * partsCut)) : null;

  let tone, badge, action;
  if (vbMeasured >= vbMax || lifePct >= 100) {
    tone = 'red'; badge = 'REPLACE NOW'; action = 'Wear land beyond limit — change insert before next cycle.';
  } else if (vbMeasured >= vbWarn || lifePct >= 80) {
    tone = 'amber'; badge = 'PLAN REPLACEMENT'; action = `Reaching wear limit. ~${remainingParts ?? '—'} parts of useful life remaining.`;
  } else {
    tone = 'green'; badge = 'HEALTHY'; action = `Insert in good condition. Continue running, recheck after ${Math.round(partsLimit * 0.2)} more parts.`;
  }
  const toneColor = tone === 'red' ? '#dc2626' : tone === 'amber' ? '#f59e0b' : '#16a34a';
  const toneBg = tone === 'red' ? '#fee2e2' : tone === 'amber' ? '#fef3c7' : '#dcfce7';

  const save = async () => {
    const entry = {
      id: 'TW-' + Date.now(), ts: Date.now(),
      machine, toolNo, toolType,
      vbMeasured: Number(vbMeasured), vbWarn: Number(vbWarn), vbMax: Number(vbMax),
      partsCut: Number(partsCut), partsLimit: Number(partsLimit),
      verdict: badge, remainingParts, notes,
    };
    const next = [entry, ...history].slice(0, 200);
    setHistory(next);
    try { await shiftadd({ [STORAGE_KEY]: next }, cid, 'SERVER_SCOPE'); } catch (e) { console.error(e); }
    setNotes('');
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: 'background.default', minHeight: '100vh' }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <BuildIcon sx={{ fontSize: 28, color: 'primary.main' }} />
        <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary' }}>Tool Wear Detection</Typography>
        <Chip label={`${history.length} inspections`} size="small" sx={{ ml: 'auto' }} />
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        {/* Setup + measurement */}
        <Card sx={{ p: 2.5, flex: 1, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <Typography sx={{ fontSize: 11, fontWeight: 800, color: 'text.secondary', letterSpacing: 1.2, mb: 2 }}>TOOL UNDER INSPECTION</Typography>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1.5}>
              <TextField select label="Machine" value={machine} onChange={(e) => setMachine(e.target.value)} size="small" sx={{ flex: 1 }}>
                {MACHINES.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>
              <TextField label="Tool no." value={toolNo} onChange={(e) => setToolNo(e.target.value)} size="small" sx={{ flex: 1 }} />
            </Stack>
            <TextField select label="Tool type" value={toolType} onChange={(e) => setToolType(e.target.value)} size="small">
              {TOOL_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <Stack direction="row" spacing={1.5}>
              <TextField label="VB measured (mm)" type="number" value={vbMeasured} onChange={(e) => setVbMeasured(Number(e.target.value) || 0)} size="small" inputProps={{ step: 0.01 }} sx={{ flex: 1, '& input': { fontSize: 18, fontWeight: 800, fontFamily: 'monospace' } }} />
              <TextField label="Warn at VB (mm)" type="number" value={vbWarn} onChange={(e) => setVbWarn(Number(e.target.value) || 0)} size="small" inputProps={{ step: 0.01 }} sx={{ flex: 1 }} />
              <TextField label="Replace at VB (mm)" type="number" value={vbMax} onChange={(e) => setVbMax(Number(e.target.value) || 0)} size="small" inputProps={{ step: 0.01 }} sx={{ flex: 1 }} />
            </Stack>
            <Stack direction="row" spacing={1.5}>
              <TextField label="Parts cut" type="number" value={partsCut} onChange={(e) => setPartsCut(Number(e.target.value) || 0)} size="small" sx={{ flex: 1 }} />
              <TextField label="Parts limit (catalog)" type="number" value={partsLimit} onChange={(e) => setPartsLimit(Number(e.target.value) || 0)} size="small" sx={{ flex: 1 }} />
            </Stack>
            <TextField label="Inspection notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} size="small" multiline rows={2} />
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" size="small" startIcon={<CameraAltIcon />} sx={{ textTransform: 'none' }}>Capture wear photo</Button>
              <Box sx={{ flexGrow: 1 }} />
              <Button variant="contained" color="primary" onClick={save} sx={{ textTransform: 'none', fontWeight: 800, px: 3 }}>
                Save inspection
              </Button>
            </Stack>
          </Stack>
        </Card>

        {/* Verdict */}
        <Card sx={{ p: 2.5, width: { xs: '100%', md: 380 }, borderRadius: 3, border: `3px solid ${toneColor}`, bgcolor: toneBg, display: 'flex', flexDirection: 'column' }}>
          <Typography sx={{ fontSize: 11, fontWeight: 800, color: toneColor, letterSpacing: 1.2 }}>VERDICT</Typography>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 1 }}>
            {tone === 'red' ? <WarningAmberIcon sx={{ fontSize: 36, color: toneColor }} /> :
             tone === 'amber' ? <WarningAmberIcon sx={{ fontSize: 36, color: toneColor }} /> :
             <CheckCircleOutlineIcon sx={{ fontSize: 36, color: toneColor }} />}
            <Typography sx={{ fontSize: 26, fontWeight: 900, color: toneColor, lineHeight: 1.1 }}>{badge}</Typography>
          </Stack>
          <Typography sx={{ fontSize: 13, color: toneColor, fontWeight: 700, mt: 1.5, opacity: 0.9 }}>
            {action}
          </Typography>

          {/* VB bar */}
          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'background.paper', borderRadius: 2 }}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 800, color: 'text.secondary', letterSpacing: 0.5 }}>WEAR LAND VB</Typography>
              <Typography sx={{ fontSize: 11, fontWeight: 800, color: toneColor }}>
                {vbMeasured.toFixed(2)} / {vbMax.toFixed(2)} mm
              </Typography>
            </Stack>
            <LinearProgress variant="determinate" value={vbPct} sx={{
              height: 12, borderRadius: 6, bgcolor: 'action.hover',
              '& .MuiLinearProgress-bar': { bgcolor: toneColor, borderRadius: 6 },
            }} />
          </Box>

          {/* Life bar */}
          <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'background.paper', borderRadius: 2 }}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 800, color: 'text.secondary', letterSpacing: 0.5 }}>LIFE USED</Typography>
              <Typography sx={{ fontSize: 11, fontWeight: 800, color: toneColor }}>
                {partsCut} / {partsLimit} parts
              </Typography>
            </Stack>
            <LinearProgress variant="determinate" value={lifePct} sx={{
              height: 12, borderRadius: 6, bgcolor: 'action.hover',
              '& .MuiLinearProgress-bar': { bgcolor: toneColor, borderRadius: 6 },
            }} />
          </Box>

          {remainingParts !== null && (
            <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'background.paper', borderRadius: 2, textAlign: 'center' }}>
              <Typography sx={{ fontSize: 11, fontWeight: 800, color: 'text.secondary', letterSpacing: 0.5 }}>PREDICTED REMAINING LIFE</Typography>
              <Typography sx={{ fontSize: 26, fontWeight: 900, color: toneColor, fontFamily: 'monospace' }}>
                ~{remainingParts} parts
              </Typography>
            </Box>
          )}
        </Card>
      </Stack>

      {/* Recent inspections */}
      <Card sx={{ p: 2, mt: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: 'text.secondary', mb: 1.5, letterSpacing: 1 }}>
          RECENT TOOL INSPECTIONS
        </Typography>
        {history.length === 0 && <Typography sx={{ color: 'text.disabled', fontSize: 13, py: 2 }}>No inspections yet.</Typography>}
        {history.slice(0, 20).map(h => {
          const vColor = h.verdict === 'REPLACE NOW' ? '#dc2626' : h.verdict === 'PLAN REPLACEMENT' ? '#f59e0b' : '#16a34a';
          const vBg   = h.verdict === 'REPLACE NOW' ? '#fee2e2' : h.verdict === 'PLAN REPLACEMENT' ? '#fef3c7' : '#dcfce7';
          return (
            <Box key={h.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Chip label={h.verdict} size="small" sx={{ bgcolor: vBg, color: vColor, fontWeight: 800, minWidth: 150 }} />
              <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13 }}>{h.machine} · {h.toolNo}</Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{h.toolType}</Typography>
              <Typography sx={{ fontFamily: 'monospace', fontSize: 13, color: vColor, ml: 'auto' }}>
                VB {Number(h.vbMeasured).toFixed(2)} · {h.partsCut}/{h.partsLimit}
              </Typography>
              <Typography sx={{ fontSize: 11, color: 'text.disabled', minWidth: 120, textAlign: 'right' }}>
                {new Date(h.ts).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}
              </Typography>
            </Box>
          );
        })}
      </Card>
    </Box>
  );
};

export default ToolWearScreen;
